import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
} from "lift-ui";
import { Loader2, GripVertical } from "lucide-react";
import { SortableCardList } from "@/components/sortable-card-list";
import { PantryItem, updatePantrySortOrder } from "../../api";

interface ReorderPantryDialogProps {
  open: boolean;
  onClose: () => void;
  pantryItems: PantryItem[];
  onReorderComplete?: () => void;
}

interface PantryItemWithId extends Omit<PantryItem, 'id'> {
  id: string;
}

const ReorderPantryDialog: React.FC<ReorderPantryDialogProps> = ({
  open,
  onClose,
  pantryItems,
  onReorderComplete,
}) => {
  const [sortedPantryItems, setSortedPantryItems] = useState<PantryItemWithId[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize sorted pantry items from props
  useEffect(() => {
    if (open && pantryItems.length > 0) {
      // Map pantry items to include string id field for SortableCardList
      const itemsWithId: PantryItemWithId[] = pantryItems.map((item) => ({
        ...item,
        id: String(item.id),
      }));

      // Sort by existing sort_order field
      const sorted = [...itemsWithId].sort((a, b) => {
        const orderA = a.sort_order;
        const orderB = b.sort_order;

        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;
        return 0;
      });

      setSortedPantryItems(sorted);
      setHasChanges(false);
    }
  }, [open, pantryItems]);

  const handleReorder = (ids: string[]) => {
    const newOrder = ids.map((id) => sortedPantryItems.find((item) => item.id === id)!);
    setSortedPantryItems(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare the sort order items with 0-based indices and integer IDs
      const sortOrderItems = sortedPantryItems.map((item, index) => ({
        id: Number(item.id),
        sort_order: index,
      }));

      // Call the API to update sort order
      await updatePantrySortOrder(sortOrderItems);

      setHasChanges(false);

      // Notify parent to refresh data
      if (onReorderComplete) {
        onReorderComplete();
      }

      // Close the dialog
      onClose();
    } catch (error) {
      console.error("Error saving pantry order:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setHasChanges(false);
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleCancel}>
      <SheetContent side="right" className="w-full sm:max-w-[768px] sm:w-[768px] flex flex-col">
        <SheetHeader>
          <SheetTitle>
            <div className="flex items-center gap-2">
              <GripVertical className="h-5 w-5" />
              <span>Reorder Pantry Items</span>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div>
            <p className="text-sm text-gray-600">
              Drag and drop cards to reorder pantry items. Changes will be saved when you click "Save Order".
            </p>
          </div>

          {sortedPantryItems.length === 0 ? (
            <div className="flex justify-center items-center min-h-[300px]">
              <p className="text-gray-600">
                No pantry items available
              </p>
            </div>
          ) : (
            <div className="min-h-[500px] px-4">
              <SortableCardList
                items={sortedPantryItems}
                gridCols={3}
                renderCard={(item, index) => (
                  <PantryCard item={item} order={index + 1} />
                )}
                onReorder={handleReorder}
                disabled={saving}
              />
            </div>
          )}
        </div>

        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving || sortedPantryItems.length === 0}
            variant="default"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Order"
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

function PantryCard({ item, order }: { item: PantryItemWithId; order: number }) {
  const title = item.metadata?.title || item.file_name;
  const isUrl = item.metadata?.isUrl || false;

  // Extract domain and generate favicon URL for URLs
  const extractDomain = (url: string | null): string | null => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (error) {
      return null;
    }
  };

  const getFaviconUrl = (url: string | null): string | null => {
    const domain = extractDomain(url);
    if (!domain) return null;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  };

  const faviconUrl = isUrl ? getFaviconUrl(item.source_url) : null;

  return (
    <div className="relative w-full min-w-0 my-4 scale-100 transition-all duration-300 hover:scale-[1.01] hover:z-[5]">
      {/* Modern Order Badge */}
      <div className="absolute -top-1.5 -left-1.5 z-[15]">
        <div className="order-badge bg-primary text-primary-foreground font-bold text-xs px-2 py-1 rounded-xl shadow-md border-2 border-white min-w-[28px] text-center transition-all duration-300">
          #{order}
        </div>
      </div>

      {/* Modern Drag Handle */}
      <div className="drag-handle absolute -top-1.5 -right-1.5 z-[15] bg-white rounded-xl h-8 w-8 inline-flex items-center justify-center shadow-sm border border-gray-100 cursor-grab transition-all duration-300 hover:bg-gray-50 hover:shadow-md active:cursor-grabbing active:scale-95">
        <GripVertical className="text-slate-500 h-4 w-4 transition-all duration-300" />
      </div>

      {/* Pantry Card */}
      <div className="w-full min-h-[160px] p-3 rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border border-gray-100 flex flex-col justify-between bg-white shadow-sm">
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-1.5 mb-1">
            {faviconUrl && (
              <img
                src={faviconUrl}
                alt="Site favicon"
                className="w-4 h-4 object-contain flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <h6 className="font-semibold text-base overflow-hidden text-ellipsis whitespace-nowrap flex-1">
              {title}
            </h6>
          </div>
          <p className="text-xs text-gray-600 mb-1">
            {isUrl ? "🔗 URL" : "📄 File"}
          </p>
          {item.metadata?.description && (
            <p className="text-xs text-gray-600 line-clamp-2 leading-tight">
              {item.metadata.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReorderPantryDialog;
